import * as React from "react"

import { ALFRED_TOUR_ACTION_EVENT, type AlfredTourAction } from "@/lib/alfred-tour"

interface TourDevice {
  id: number
  deviceId: string
}

interface BulkPreviewSnapshot {
  selectionScope: string
  selectedDevices: ReadonlyArray<readonly [number, string]>
  bulkEditorOpen: boolean
}

interface UseAlfredTourBulkPreviewOptions {
  devices: ReadonlyArray<TourDevice>
  selectedDevices: ReadonlyMap<number, string>
  selectionScope: string
  bulkEditorOpen: boolean
  setBulkEditorOpen: (open: boolean) => void
  clearSelectedDevices: () => void
  replaceSelectedDevicesForScope: (
    expectedScopeKey: string,
    entries: ReadonlyArray<readonly [number, string]>,
  ) => void
}

export function useAlfredTourBulkPreview({
  devices,
  selectedDevices,
  selectionScope,
  bulkEditorOpen,
  setBulkEditorOpen,
  clearSelectedDevices,
  replaceSelectedDevicesForScope,
}: UseAlfredTourBulkPreviewOptions) {
  const previewSnapshot = React.useRef<BulkPreviewSnapshot | null>(null)

  React.useEffect(() => {
    const selectFirstDevice = () => {
      const firstDevice = devices[0]
      if (!firstDevice) return false
      replaceSelectedDevicesForScope(selectionScope, [[firstDevice.id, firstDevice.deviceId]])
      return true
    }

    const handleTourAction = (event: Event) => {
      const action = (event as CustomEvent<AlfredTourAction>).detail

      if (action === "show-bulk-actions") {
        setBulkEditorOpen(false)
        selectFirstDevice()
        return
      }

      if (action === "preview-bulk-actions") {
        if (!previewSnapshot.current) {
          previewSnapshot.current = {
            selectionScope,
            selectedDevices: [...selectedDevices],
            bulkEditorOpen,
          }
        }
        if (!bulkEditorOpen && selectedDevices.size === 0) selectFirstDevice()
        return
      }

      if (action === "exit-bulk-preview") {
        const snapshot = previewSnapshot.current
        previewSnapshot.current = null
        if (!snapshot || snapshot.selectionScope !== selectionScope) return
        replaceSelectedDevicesForScope(selectionScope, snapshot.selectedDevices)
        setBulkEditorOpen(snapshot.bulkEditorOpen && snapshot.selectedDevices.length > 0)
        return
      }

      if (action === "exit-bulk") {
        previewSnapshot.current = null
        setBulkEditorOpen(false)
        clearSelectedDevices()
      }
    }

    window.addEventListener(ALFRED_TOUR_ACTION_EVENT, handleTourAction)
    return () => window.removeEventListener(ALFRED_TOUR_ACTION_EVENT, handleTourAction)
  }, [
    bulkEditorOpen,
    clearSelectedDevices,
    devices,
    replaceSelectedDevicesForScope,
    selectedDevices,
    selectionScope,
    setBulkEditorOpen,
  ])
}
